import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Image as ChakraImage,
  Text,
  Button,
  VStack,
  HStack,
  Heading,
  Input,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Badge,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaTimes, FaRedo, FaArrowRight, FaCoins } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const CardGuessGame = ({ onClose }) => {
  const { isAuthenticated, userData } = useAuth();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(100);
  const [revealedPieces, setRevealedPieces] = useState(new Set());
  const [letterInputs, setLetterInputs] = useState([]);
  const [letterIds, setLetterIds] = useState([]);
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'revealed'
  const [error, setError] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [gameFinished, setGameFinished] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const inputRefs = useRef([]);
  const canvasRef = useRef(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('#ffcc00', '#ffcc00');

  // Dimensioni carta: 672x936px
  const CARD_WIDTH = 672;
  const CARD_HEIGHT = 936;

  // Definizione delle zone (coordinate in percentuali)
  const PIECES = {
    mana: {
      name: 'Mana',
      x: 50, // da x 50% a 100%
      y: 0, // da y 0% a 20%
      width: 55,
      height: 10,
    },
    type: {
      name: 'Tipo e Sottotipo',
      x: 0, // da x 0% a 100%
      y: 56, // da y 48% a 51.2% (circa 30px su 936px = 3.2%)
      width: 100,
      height: 5,
    },
    art: {
      name: 'Art',
      x: 5, // da x 5% a 95% (lascia margini laterali)
      y: 20, // da y 20% a 55% (area centrale dell'arte)
      width: 90,
      height: 35,
    },
    text: {
      name: 'Testo',
      x: 0, // da x 0% a 100%
      y: 62, // da y 48% a 65%
      width: 100,
      height: 17,
    },
    stats: {
      name: 'F/C',
      x: 78, // da x 78% a 100% (78 + 22 = 100)
      y: 85, // da y 85% a 100% (85 + 15 = 100)
      width: 22,
      height: 15,
    },
    reveal: {
      name: 'Svela Carta',
      x: 0, // da x 0% a 100%
      y: 0, // da y 0% a 100%
      width: 100,
      height: 100,
    },
  };

  // Funzione per normalizzare il nome (rimuove accenti, spazi extra, case-insensitive)
  const normalizeName = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Fetch carta casuale da Scryfall
  const fetchRandomCard = async (keepTotalScore = false) => {
    try {
      setLoading(true);
      setError(null);
      setGameState('playing');
      setScore(100);
      setRevealedPieces(new Set());
      setLetterInputs([]);
      setLetterIds([]);

      // Se non stiamo mantenendo il punteggio totale, resettalo (solo alla prima carta)
      if (!keepTotalScore) {
        setTotalScore(0);
        setCurrentCardIndex(0);
        setGameFinished(false);
        setGameHistory([]);
      }

      const response = await fetch('https://api.scryfall.com/cards/random');

      if (!response.ok) {
        throw new Error('Errore nel caricamento della carta');
      }

      const card = await response.json();

      // Verifica che la carta abbia un'immagine
      if (!card.image_uris || !card.image_uris.normal) {
        throw new Error('Carta senza immagine disponibile');
      }

      setCardData(card);

      // Rivelare automaticamente un pezzo random
      const pieceKeys = Object.keys(PIECES);
      const randomPieceKey =
        pieceKeys[Math.floor(Math.random() * pieceKeys.length)];
      setRevealedPieces(new Set([randomPieceKey]));

      // Inizializza le caselle lettera per lettera
      const cardName = card.name || '';
      const nameLength = cardName.length;
      const initialInputs = Array(nameLength).fill('');

      // Aggiungi un suggerimento random: una lettera random in una posizione random
      if (nameLength > 0) {
        // Trova tutte le posizioni che non sono spazi
        const nonSpaceIndices = [];
        for (let i = 0; i < nameLength; i += 1) {
          if (cardName[i] !== ' ') {
            nonSpaceIndices.push(i);
          }
        }

        // Se ci sono posizioni valide, scegli una random
        if (nonSpaceIndices.length > 0) {
          const randomIndex =
            nonSpaceIndices[Math.floor(Math.random() * nonSpaceIndices.length)];
          initialInputs[randomIndex] = cardName[randomIndex].toUpperCase();
        }
      }

      setLetterInputs(initialInputs);
      // Crea id unici per ogni posizione basati sul nome della carta
      setLetterIds(
        Array(nameLength)
          .fill(null)
          .map((_, i) => `letter-${cardName}-pos-${i}-${Date.now()}`)
      );
      inputRefs.current = Array(nameLength)
        .fill(null)
        .map(() => React.createRef());
    } catch (err) {
      console.error('Error fetching card:', err);
      setError(err.message || 'Errore nel caricamento della carta');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRandomCard();
  }, []);

  // Calcola dimensioni responsive
  const cardScale = useBreakpointValue({
    base: 0.4,
    sm: 0.5,
    md: 0.6,
    lg: 0.6,
  });
  const displayWidth = CARD_WIDTH * (cardScale || 0.6);

  // Funzione per disegnare i pezzi rivelati sul canvas
  useEffect(() => {
    if (!cardData || !canvasRef.current || revealedPieces.size === 0) return;

    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    if (!container) return;

    const ctx = canvas.getContext('2d');

    // Usa le dimensioni effettive del container per mantenere le proporzioni
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Imposta le dimensioni del canvas alle dimensioni effettive del container
    canvas.width = containerWidth;
    canvas.height = containerHeight;

    // Pulisci il canvas con sfondo trasparente
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Carica l'immagine (usa il costruttore nativo del browser)
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Calcola le dimensioni per centrare l'immagine mantenendo l'aspect ratio
      const imgAspectRatio = img.width / img.height;
      const canvasAspectRatio = containerWidth / containerHeight;

      let drawWidth;
      let drawHeight;
      let offsetX;
      let offsetY;

      if (imgAspectRatio > canvasAspectRatio) {
        // L'immagine è più larga, usa la larghezza del container
        drawWidth = containerWidth;
        drawHeight = containerWidth / imgAspectRatio;
        offsetX = 0;
        offsetY = (containerHeight - drawHeight) / 2;
      } else {
        // L'immagine è più alta, usa l'altezza del container
        drawHeight = containerHeight;
        drawWidth = containerHeight * imgAspectRatio;
        offsetX = (containerWidth - drawWidth) / 2;
        offsetY = 0;
      }

      // Disegna solo i pezzi rivelati
      // Le zone sono definite in percentuale rispetto alle dimensioni teoriche della carta (672x936)
      Array.from(revealedPieces).forEach((pieceKey) => {
        const piece = PIECES[pieceKey];

        // Coordinate percentuali rispetto alle dimensioni teoriche della carta (672x936)
        const pieceXPercent = piece.x / 100;
        const pieceYPercent = piece.y / 100;
        const pieceWidthPercent = piece.width / 100;
        const pieceHeightPercent = piece.height / 100;

        // Coordinate nell'immagine caricata (basate sulle proporzioni teoriche)
        // Assumiamo che l'immagine abbia le stesse proporzioni della carta teorica
        const sx = pieceXPercent * img.width;
        const sy = pieceYPercent * img.height;
        const sw = pieceWidthPercent * img.width;
        const sh = pieceHeightPercent * img.height;

        // Coordinate nel canvas (scalate e centrate)
        // Le coordinate percentuali vengono applicate all'area disegnata (drawWidth x drawHeight)
        const dx = offsetX + pieceXPercent * drawWidth;
        const dy = offsetY + pieceYPercent * drawHeight;
        const dw = pieceWidthPercent * drawWidth;
        const dh = pieceHeightPercent * drawHeight;

        ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
      });
    };
    img.src = cardData.image_uris.normal;
  }, [cardData, revealedPieces]);

  // Funzione per richiedere un pezzo
  const requestPiece = (pieceKey) => {
    if (revealedPieces.has(pieceKey) || gameState !== 'playing') return;

    // Penalità diversa per ogni tipo di pezzo
    let penalty = 5; // default
    if (pieceKey === 'text') {
      penalty = 20;
    } else if (pieceKey === 'art') {
      penalty = 15;
    } else if (pieceKey === 'reveal') {
      penalty = 100;
    }

    setRevealedPieces((prev) => new Set([...prev, pieceKey]));
    setScore((prev) => Math.max(0, prev - penalty));
  };

  // Funzione per registrare il risultato del gioco come activity log
  const logGameResult = useCallback(
    async (gameData) => {
      // Solo per utenti loggati
      if (!isAuthenticated || !userData) {
        return;
      }

      // Verifica che ci sia almeno un socio
      if (!userData.tesseramenti || userData.tesseramenti.length === 0) {
        return;
      }

      try {
        const firstSocio = userData.tesseramenti[0];
        const nomeSocio = firstSocio.nomeSocio || 'Utente';
        const socioDocumentId = firstSocio.documentId;

        // Prepara i dati del metadata
        const metadata = {
          totalScore: gameData.totalScore,
          gameHistory: gameData.gameHistory,
          cardsPlayed: gameData.cardsPlayed || 5,
        };

        // Chiama l'endpoint per creare l'activity log
        await axios.post(
          '/api/game/activity-logs',
          {
            action: 'Nuovo punteggio Indovina la Carta',
            description: `${nomeSocio} ha totalizzato ${gameData.totalScore} punti in Indovina la Carta`,
            category: 'game',
            socio: socioDocumentId,
            metadata,
          },
          {
            withCredentials: true,
          }
        );
      } catch (err) {
        // Gestisci errori silenziosamente per non interrompere l'esperienza di gioco
        console.error('Errore nel logging del risultato del gioco:', err);
      }
    },
    [isAuthenticated, userData]
  );

  // Funzione per terminare il gioco
  const finishGame = () => {
    // Salva l'ultima carta nella storia
    setGameHistory((prev) => {
      const updatedHistory = [
        ...prev,
        {
          cardName: cardData.name,
          score,
          guessed: gameState === 'won',
          revealed: gameState === 'revealed',
        },
      ];

      // Registra il risultato del gioco
      logGameResult({
        totalScore,
        gameHistory: updatedHistory,
        cardsPlayed: 5,
      });

      return updatedHistory;
    });
    setGameFinished(true);
  };

  // Funzione per verificare il guess
  const checkGuess = (guessToCheck) => {
    if (!cardData || gameState !== 'playing') return;

    // Rimuovi gli spazi da entrambi i nomi per il confronto
    const normalizedGuess = normalizeName(guessToCheck).replace(/\s+/g, '');
    const normalizedCardName = normalizeName(cardData.name).replace(/\s+/g, '');

    if (normalizedGuess === normalizedCardName) {
      setGameState('won');
      // Bonus per aver indovinato: aggiungi 150 punti
      const finalScore = score + 150;
      setScore(finalScore);
      // Aggiungi il punteggio finale al totale
      setTotalScore((prev) => prev + finalScore);

      // Se è l'ultima carta, termina il gioco
      if (currentCardIndex >= 4) {
        setTimeout(() => {
          finishGame();
        }, 1000);
      }
    }
  };

  // Funzione per comprare una lettera (10 punti)
  const buyLetter = () => {
    if (gameState !== 'playing' || !cardData || score < 10) return;

    const cardName = cardData.name || '';
    const nameLength = cardName.length;

    // Trova tutte le posizioni vuote (non spazi e non già riempite)
    const emptyIndices = [];
    for (let i = 0; i < nameLength; i += 1) {
      if (cardName[i] !== ' ' && !letterInputs[i]) {
        emptyIndices.push(i);
      }
    }

    // Se ci sono posizioni vuote, scegli una random e riempila
    if (emptyIndices.length > 0) {
      const randomIndex =
        emptyIndices[Math.floor(Math.random() * emptyIndices.length)];
      const newInputs = [...letterInputs];
      newInputs[randomIndex] = cardName[randomIndex].toUpperCase();
      setLetterInputs(newInputs);

      // Sottrai 10 punti
      setScore((prev) => Math.max(0, prev - 10));

      // Verifica se tutte le lettere (non spazi) sono state inserite
      const fullGuess = newInputs.join('');
      // Conta solo le lettere non spazi inserite
      const lettersEntered = newInputs.filter((letter, idx) => {
        return letter && cardName[idx] !== ' ';
      }).length;
      // Conta le lettere totali (non spazi) nel nome della carta
      const totalLetters = cardName
        .split('')
        .filter((char) => char !== ' ').length;

      // Verifica se tutte le lettere sono state inserite (ignorando gli spazi)
      if (lettersEntered === totalLetters && totalLetters > 0) {
        checkGuess(fullGuess);
      }
    }
  };

  // Funzione per svelare la carta completa
  const revealCard = () => {
    if (gameState !== 'playing') return;

    // Richiedi il pezzo "reveal" che copre tutta la carta
    requestPiece('reveal');

    // Riempie automaticamente tutte le lettere mancanti
    if (cardData) {
      const cardName = cardData.name || '';
      const nameLength = cardName.length;
      const filledInputs = [];

      for (let i = 0; i < nameLength; i += 1) {
        if (cardName[i] === ' ') {
          filledInputs[i] = ' ';
        } else {
          filledInputs[i] = cardName[i].toUpperCase();
        }
      }

      setLetterInputs(filledInputs);
    }

    // Penalità per svelare la carta: 100 punti
    const finalScore = Math.max(0, score - 100);
    setScore(finalScore);

    // Aggiungi il punteggio corrente al totale (anche se 0 o negativo)
    setTotalScore((prev) => prev + finalScore);

    // Se è l'ultima carta, termina il gioco dopo un breve delay
    if (currentCardIndex >= 4) {
      setTimeout(() => {
        finishGame();
      }, 1000);
    }
  };

  // Funzione per passare alla carta successiva
  const nextCard = () => {
    if (currentCardIndex < 4) {
      // Salva la carta corrente nella storia
      setGameHistory((prev) => [
        ...prev,
        {
          cardName: cardData.name,
          score,
          guessed: gameState === 'won',
          revealed: gameState === 'revealed',
        },
      ]);
      setCurrentCardIndex((prev) => prev + 1);
      fetchRandomCard(true); // true = mantieni il punteggio totale
    }
  };

  // Funzione per gestire l'input delle lettere
  const handleLetterInput = (index, value) => {
    if (gameState !== 'playing') return;

    const newInputs = [...letterInputs];
    // Prendi solo l'ultimo carattere inserito
    newInputs[index] = value.slice(-1).toUpperCase();
    setLetterInputs(newInputs);

    // Auto-focus sul prossimo input (salta gli spazi)
    if (value && index < letterInputs.length - 1) {
      let nextIndex = index + 1;
      // Salta gli spazi
      while (
        nextIndex < letterInputs.length &&
        cardData?.name[nextIndex] === ' '
      ) {
        nextIndex += 1;
      }
      if (nextIndex < letterInputs.length) {
        inputRefs.current[nextIndex]?.current?.focus();
      }
    }

    // Verifica se tutte le lettere (non spazi) sono state inserite
    const fullGuess = newInputs.join('');
    // Conta solo le lettere non spazi inserite
    const lettersEntered = newInputs.filter((letter, idx) => {
      return letter && cardData?.name[idx] !== ' ';
    }).length;
    // Conta le lettere totali (non spazi) nel nome della carta
    const totalLetters = cardData?.name
      ? cardData.name.split('').filter((char) => char !== ' ').length
      : 0;

    // Verifica se tutte le lettere sono state inserite (ignorando gli spazi)
    if (lettersEntered === totalLetters && totalLetters > 0) {
      checkGuess(fullGuess);
    }
  };

  // Funzione per gestire il backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !letterInputs[index] && index > 0) {
      inputRefs.current[index - 1]?.current?.focus();
    }
  };

  // Funzione per evidenziare le lettere corrette
  const getLetterStatus = (index) => {
    if (!cardData || gameState !== 'won') return 'default';

    const cardName = cardData.name.toUpperCase();
    const inputLetter = letterInputs[index]?.toUpperCase();
    const correctLetter = cardName[index]?.toUpperCase();

    if (inputLetter === correctLetter) {
      return 'correct';
    }
    return 'incorrect';
  };

  // Funzione per raggruppare gli input per parola (separati da spazi)
  const groupInputsByWords = () => {
    if (!cardData) return [];

    const { name: cardName = '' } = cardData;
    const words = [];
    let currentWord = [];
    let currentWordIndices = [];

    for (let i = 0; i < letterInputs.length; i += 1) {
      if (cardName[i] === ' ') {
        if (currentWord.length > 0) {
          words.push({ indices: currentWordIndices, inputs: currentWord });
          currentWord = [];
          currentWordIndices = [];
        }
      } else {
        currentWord.push({ index: i, value: letterInputs[i] });
        currentWordIndices.push(i);
      }
    }

    // Aggiungi l'ultima parola se presente
    if (currentWord.length > 0) {
      words.push({ indices: currentWordIndices, inputs: currentWord });
    }

    return words;
  };

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack spacing={4}>
          <Spinner size="xl" color={accentColor} />
          <Text color="white">Caricamento carta...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        minH="100vh"
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Box bg={bgColor} p={8} borderRadius="lg" maxW="500px" w="full">
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
          <HStack spacing={4} justify="center">
            <Button onClick={fetchRandomCard} colorScheme="yellow">
              Riprova
            </Button>
            {onClose && (
              <Button onClick={onClose} variant="outline">
                Chiudi
              </Button>
            )}
          </HStack>
        </Box>
      </Box>
    );
  }

  if (!cardData) {
    return null;
  }

  return (
    <Box minH="100vh" bg={bgColor} p={{ base: 2, md: 4 }}>
      <Box
        maxW="1200px"
        mx="auto"
        bg={bgColor}
        borderRadius="lg"
        p={{ base: 4, md: 6 }}
        mt={{ base: 4, md: 8 }}
        mb={4}
        w="100%"
        overflowX="hidden"
        boxShadow="lg"
      >
        {/* Header */}
        <VStack spacing={4} mb={6} align="stretch">
          <HStack justify="center" align="center" position="relative" w="100%">
            <Heading size="lg" color={textColor}>
              Indovina la Carta
            </Heading>
            {onClose && (
              <IconButton
                icon={<FaTimes />}
                onClick={onClose}
                aria-label="Chiudi"
                variant="ghost"
                position="absolute"
                right={0}
              />
            )}
          </HStack>
        </VStack>

        {/* Game Finished Message */}
        {gameFinished && (
          <Box mb={6}>
            <Alert status="success" mb={4} borderRadius="md">
              <AlertIcon />
              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="lg">
                  🎉 Gioco Completato!
                </Text>
                <Text>Hai completato tutte e 5 le carte!</Text>
                <Text fontSize="xl" fontWeight="bold" color="green.600">
                  Punteggio Finale: {totalScore} punti
                </Text>
              </VStack>
            </Alert>

            {/* Riepilogo Partita */}
            <Box
              bg="gray.50"
              p={6}
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <Heading size="md" mb={4} color={textColor}>
                Riepilogo Partita
              </Heading>
              <VStack spacing={3} align="stretch">
                {gameHistory.map((card) => (
                  <HStack
                    key={`card-${card.cardName}-${card.score}-${card.guessed ? 'guessed' : 'revealed'}`}
                    justify="space-between"
                    p={3}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontWeight="bold" fontSize="md">
                        Carta {gameHistory.indexOf(card) + 1}: {card.cardName}
                      </Text>
                      <HStack spacing={2}>
                        {card.guessed && (
                          <Badge colorScheme="green" fontSize="xs">
                            ✓ Indovinata
                          </Badge>
                        )}
                        {card.revealed && (
                          <Badge colorScheme="orange" fontSize="xs">
                            Svelata
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                    <Badge
                      colorScheme={card.score > 0 ? 'green' : 'red'}
                      fontSize="lg"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {card.score > 0 ? '+' : ''}
                      {card.score} punti
                    </Badge>
                  </HStack>
                ))}
                <Box mt={4} pt={4} borderTop="2px solid" borderColor="gray.300">
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold">
                      Punteggio Totale:
                    </Text>
                    <Badge
                      colorScheme="yellow"
                      fontSize="xl"
                      px={4}
                      py={2}
                      borderRadius="full"
                    >
                      {totalScore} punti
                    </Badge>
                  </HStack>
                </Box>
              </VStack>
            </Box>
          </Box>
        )}

        {/* Card Image with Revealed Pieces */}
        <Box mb={6} position="relative" w="100%">
          <VStack spacing={4}>
            <Box
              position="relative"
              width="100%"
              maxW={{
                base: 'min(90vw, 400px)',
                sm: `${displayWidth}px`,
                md: `${displayWidth}px`,
              }}
              aspectRatio={`${CARD_WIDTH} / ${CARD_HEIGHT}`}
              mx="auto"
            >
              {/* Canvas per mostrare i pezzi rivelati o immagine completa se vinto o se è stato rivelato il pezzo "reveal" */}
              {gameState === 'won' || revealedPieces.has('reveal') ? (
                <Box
                  width="100%"
                  height="100%"
                  border={gameState === 'won' ? '4px solid' : 'none'}
                  borderColor={
                    gameState === 'won' ? 'green.500' : 'transparent'
                  }
                  borderRadius="md"
                  overflow="hidden"
                >
                  <ChakraImage
                    src={cardData.image_uris.normal}
                    alt={cardData.name}
                    width="100%"
                    height="100%"
                    objectFit="contain"
                    display="block"
                  />
                </Box>
              ) : revealedPieces.size > 0 ? (
                <canvas
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  bg="gray.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="md"
                  border="2px solid"
                  borderColor="gray.300"
                >
                  <Text color="gray.500">Richiedi un pezzo per iniziare!</Text>
                </Box>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Piece Request Buttons */}
        {gameState === 'playing' && !gameFinished && (
          <Box mb={6}>
            <Heading size="md" mb={4} color={textColor} textAlign="center">
              Richiedi un Pezzo
            </Heading>
            <SimpleGrid columns={{ base: 3, md: 3 }} spacing={4}>
              {Object.entries(PIECES)
                .filter(([key]) => key !== 'reveal') // Escludi "reveal" dalla griglia normale
                .map(([key, piece]) => {
                  let penalty = 5; // default
                  if (key === 'text') {
                    penalty = 20;
                  } else if (key === 'art') {
                    penalty = 15;
                  }
                  const isRevealed = revealedPieces.has(key);
                  return (
                    <Button
                      key={key}
                      onClick={() => requestPiece(key)}
                      isDisabled={isRevealed}
                      colorScheme={isRevealed ? 'green' : 'yellow'}
                      variant={isRevealed ? 'solid' : 'outline'}
                      size={{ base: 'sm', md: 'md' }}
                      fontWeight="semibold"
                      borderRadius="md"
                      boxShadow={isRevealed ? 'sm' : 'md'}
                      _hover={
                        !isRevealed
                          ? { transform: 'translateY(-2px)', boxShadow: 'lg' }
                          : {}
                      }
                      transition="all 0.2s"
                    >
                      <VStack spacing={0}>
                        <Text>
                          {piece.name} {isRevealed && ' ✓'}
                        </Text>
                        {!isRevealed && (
                          <HStack spacing={1} alignItems="center">
                            <Text fontSize="sm" opacity={0.7}>
                              {penalty}
                            </Text>
                            <FaCoins size={12} opacity={0.7} />
                          </HStack>
                        )}
                      </VStack>
                    </Button>
                  );
                })}
              {/* Pulsante Aggiungi Lettera */}
              <Button
                onClick={buyLetter}
                isDisabled={gameState !== 'playing' || score < 10}
                colorScheme="purple"
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                fontWeight="semibold"
                borderRadius="md"
                boxShadow="md"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                  bg: 'purple.50',
                }}
                transition="all 0.2s"
              >
                <VStack spacing={0}>
                  <Text>Lettera</Text>
                  <HStack spacing={1} alignItems="center">
                    <Text fontSize="sm" opacity={0.7}>
                      10
                    </Text>
                    <FaCoins size={12} opacity={0.7} />
                  </HStack>
                </VStack>
              </Button>
            </SimpleGrid>
          </Box>
        )}

        {/* Letter Input Boxes */}
        {gameState === 'playing' && !gameFinished && (
          <Box mb={6}>
            <VStack spacing={4} mb={4}>
              <HStack justify="center" spacing={4} flexWrap="wrap">
                {!gameFinished && (
                  <Badge
                    fontSize={{ base: 'sm', md: 'md' }}
                    colorScheme="blue"
                    px={{ base: 2, md: 3 }}
                    py={{ base: 1, md: 1.5 }}
                    borderRadius="full"
                    fontWeight="bold"
                  >
                    Carta {currentCardIndex + 1}/5
                  </Badge>
                )}
                <Badge
                  fontSize={{ base: 'md', md: 'lg' }}
                  colorScheme="yellow"
                  px={{ base: 3, md: 4 }}
                  py={{ base: 1.5, md: 2 }}
                  borderRadius="full"
                  fontWeight="bold"
                  boxShadow="md"
                >
                  ⭐ Punteggio: {score}
                </Badge>
                {!gameFinished && totalScore > 0 && (
                  <Badge
                    fontSize={{ base: 'sm', md: 'md' }}
                    colorScheme="green"
                    px={{ base: 2, md: 3 }}
                    py={{ base: 1, md: 1.5 }}
                    borderRadius="full"
                    fontWeight="bold"
                  >
                    Totale: {totalScore}
                  </Badge>
                )}
              </HStack>
              <Heading size="md" color={textColor}>
                Inserisci il Nome
              </Heading>
            </VStack>
            <VStack spacing={3} align="center" mb={4}>
              {groupInputsByWords().map((word) => {
                const wordKey = `word-${word.indices[0]}-${word.indices[word.indices.length - 1]}`;
                return (
                  <HStack
                    key={wordKey}
                    spacing={2}
                    justify="center"
                    flexWrap="wrap"
                  >
                    {word.inputs.map((letterData) => {
                      const { index, value } = letterData;
                      return (
                        <Box
                          key={
                            letterIds[index] ||
                            `letter-${cardData?.name || 'unknown'}-${index}`
                          }
                          position="relative"
                        >
                          <Input
                            ref={(el) => {
                              inputRefs.current[index] = { current: el };
                            }}
                            value={value}
                            onChange={(e) =>
                              handleLetterInput(index, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            maxLength={1}
                            width={{ base: '35px', md: '40px' }}
                            height="auto"
                            minH={{ base: '50px', md: '60px' }}
                            textAlign="center"
                            fontSize={{ base: '2xl', md: '3xl' }}
                            fontWeight="bold"
                            textTransform="uppercase"
                            color={
                              gameState === 'won'
                                ? getLetterStatus(index) === 'correct'
                                  ? 'green.600'
                                  : 'red.600'
                                : 'gray.800'
                            }
                            bg="transparent"
                            border="none"
                            borderBottom="4px solid"
                            borderColor={
                              gameState === 'won'
                                ? getLetterStatus(index) === 'correct'
                                  ? 'green.500'
                                  : 'red.500'
                                : 'gray.400'
                            }
                            borderRadius="0"
                            px={0}
                            py={2}
                            _focus={{
                              borderColor: 'blue.500',
                              outline: 'none',
                              boxShadow: 'none',
                            }}
                            _hover={{
                              borderColor:
                                gameState === 'playing'
                                  ? 'blue.400'
                                  : undefined,
                            }}
                            isDisabled={gameState !== 'playing'}
                          />
                        </Box>
                      );
                    })}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
        )}

        {/* Reveal Card Button */}
        {gameState === 'playing' && !gameFinished && (
          <Box mb={6} textAlign="center">
            <Button
              onClick={revealCard}
              colorScheme="red"
              size={{ base: 'md', md: 'lg' }}
              variant="outline"
              fontWeight="bold"
              borderRadius="md"
              boxShadow="md"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
                bg: 'red.50',
              }}
              transition="all 0.2s"
            >
              🔍 Svela Carta (-100 punti)
            </Button>
          </Box>
        )}

        {/* Action Buttons */}
        <HStack spacing={4} justify="center" mt={6} flexWrap="wrap">
          {gameFinished ? (
            <Button
              leftIcon={<FaRedo />}
              onClick={() => fetchRandomCard(false)}
              colorScheme="blue"
              size={{ base: 'md', md: 'lg' }}
              fontWeight="semibold"
              borderRadius="md"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Nuovo Gioco
            </Button>
          ) : (gameState === 'won' ||
              gameState === 'revealed' ||
              revealedPieces.has('reveal')) &&
            currentCardIndex < 4 ? (
            <Button
              rightIcon={<FaArrowRight />}
              onClick={nextCard}
              colorScheme="green"
              size={{ base: 'md', md: 'lg' }}
              fontWeight="semibold"
              borderRadius="md"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Prossima Carta
            </Button>
          ) : (gameState === 'won' ||
              gameState === 'revealed' ||
              revealedPieces.has('reveal')) &&
            currentCardIndex >= 4 ? (
            <Button
              onClick={finishGame}
              colorScheme="purple"
              size={{ base: 'md', md: 'lg' }}
              fontWeight="semibold"
              borderRadius="md"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Termina
            </Button>
          ) : null}
        </HStack>
      </Box>
    </Box>
  );
};

export default CardGuessGame;
